/**
 * Serialize between Markdown subset (* ** _ ~ ~~ ` ```, [label](url) links, and @mentions)
 * and contenteditable HTML.
 * Uses same grammar as display (react-markdown + remarkWhatsAppPreprocess).
 * Markdown images are not converted to <img> (see remarkStripChatImages); legacy <img> in
 * the editor is serialized to plain alt or src text.
 */

import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import remarkStripChatImages from 'utils/remarkStripChatImages';
import { preprocessWhatsAppMarkdown } from 'utils/remarkWhatsAppPreprocess';

const escapeHtml = str =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const normalizeMentionDisplay = display =>
  String(display ?? '')
    .replace(/^@+/, '')
    .trim();

/** Allowed link protocols for chat composer HTML, paste sanitization, and markdown round-trip. */
export function isAllowedChatLinkHref(href) {
  return /^(https?:|mailto:)/i.test(String(href || '').trim());
}

/**
 * Add https:// for host-like input without a scheme, https: for protocol-relative //…, or mailto:
 * for a bare email. Leaves allowed hrefs unchanged; does not prepend https to other schemes (ftp:, etc.).
 */
export function normalizeChatLinkHref(input) {
  const t = String(input ?? '').trim();
  if (!t) return t;
  if (isAllowedChatLinkHref(t)) return t;
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return t;
  if (t.startsWith('//')) {
    const withScheme = `https:${t}`;
    return isAllowedChatLinkHref(withScheme) ? withScheme : t;
  }
  if (/^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/i.test(t)) {
    return `mailto:${t}`;
  }
  if (/^([a-z0-9-]+\.)+[a-z]{2,}(\/.*)?$/i.test(t) || /^localhost(:\d+)?(\/.*)?$/i.test(t)) {
    return `https://${t}`;
  }
  return t;
}

/**
 * True when clipboard plain text is a single allowed URL line (for paste-as-link on selection).
 */
export function isSinglePlainUrlForLinkPaste(text) {
  const t = String(text ?? '').trim();
  if (!t || /[\r\n]/.test(t)) return false;
  return isAllowedChatLinkHref(normalizeChatLinkHref(t));
}

function headingLevelFromTag(tag) {
  const m = /^h([1-6])$/i.exec(tag);
  return m ? parseInt(m[1], 10) : 0;
}

/** Block-level tags that become paragraph breaks (double newline) in markup. */
const BLOCK_TAGS = new Set([
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'hr',
]);

/**
 * Convert markup string to HTML for contenteditable. Mentions array items: { id, display }.
 * Returns a Promise that resolves to the HTML string (unified pipeline is async).
 */
export function markupToHtml(markup, mentions = []) {
  if (!markup) return Promise.resolve('');

  const placeholders = [];
  let text = markup;
  (mentions || []).forEach((m, i) => {
    const display = normalizeMentionDisplay(m.display);
    const needle = `@${display}`;
    const ph = `\u200B__M${i}__\u200B`;
    placeholders.push({
      ph,
      span: `<span data-mention-id="${escapeHtml(m.id)}" data-mention-display="${escapeHtml(
        display,
      )}" contenteditable="false">@${escapeHtml(display)}</span>`,
    });
    text = text.split(needle).join(ph);
  });

  const preprocessed = preprocessWhatsAppMarkdown(text);
  const processor = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkStripChatImages)
    .use(remarkRehype)
    .use(rehypeStringify);

  return processor.process(preprocessed).then(vfile => {
    let html = String(vfile.value);
    placeholders.forEach(({ ph, span }) => {
      html = html.split(ph).join(span);
    });
    return html;
  });
}

/**
 * Serialize contenteditable container to { markup, text, mentions }.
 * Uses Markdown subset: * ** _ ~ ~~ for bold/italic/strike, ` for code (inline),
 * and [label](https://...) for links produced by markupToHtml.
 */
export function contentEditableToMarkup(containerEl) {
  if (!containerEl) return { markup: '', text: '', mentions: [] };

  const mentionTag = 'span';
  const mentionAttr = 'data-mention-id';
  let markup = '';
  let text = '';
  const mentions = [];
  let plainTextIndex = 0;

  // Output single * for bold, _ italic, ~ strike, ` for code (Markdown inline)
  const formatWrap = {
    strong: '*',
    b: '*',
    em: '_',
    i: '_',
    del: '~',
    s: '~',
    strike: '~',
    code: '`',
  };

  const getMarkers = el => {
    const tag = (el.tagName || '').toLowerCase();
    const markers = [];
    const mapped = formatWrap[tag];
    if (mapped) markers.push(mapped);

    // Some browsers emit formatting as styled <span> instead of semantic tags.
    const style = el.style || {};
    const inlineStyleText = String(el.getAttribute('style') || '').toLowerCase();
    const textDecoration = String(
      style.textDecoration || style.textDecorationLine || '',
    ).toLowerCase();
    let computedTextDecoration = '';
    if (typeof window !== 'undefined' && window.getComputedStyle) {
      const computed = window.getComputedStyle(el);
      computedTextDecoration = String(
        computed?.textDecorationLine || computed?.textDecoration || '',
      ).toLowerCase();
    }
    const fontStyle = String(style.fontStyle || '').toLowerCase();
    const fontWeight = String(style.fontWeight || '').toLowerCase();
    const fontFamily = String(style.fontFamily || '').toLowerCase();

    if (
      (textDecoration.includes('line-through') ||
        inlineStyleText.includes('line-through') ||
        computedTextDecoration.includes('line-through')) &&
      !markers.includes('~')
    ) {
      markers.push('~');
    }
    if (fontStyle === 'italic' && !markers.includes('_')) markers.push('_');
    if ((fontWeight === 'bold' || Number(fontWeight) >= 600) && !markers.includes('*'))
      markers.push('*');
    if (fontFamily.includes('monospace') && !markers.includes('`')) markers.push('`');

    return markers;
  };

  /**
   * Walk a subtree and append to the given buffers. Used to collect inner content
   * of a formatting element so we can trim leading/trailing whitespace before wrapping.
   * buf.plainTextIndex is the global output index (start of this format element's content).
   */
  function walkInto(buf, node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const value = node.textContent || '';
      buf.markup += value;
      buf.text += value;
      buf.plainTextIndex += value.length;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node;
    const tag = (el.tagName || '').toLowerCase();

    if (tag === mentionTag && el.getAttribute(mentionAttr)) {
      const id = el.getAttribute(mentionAttr);
      const display = normalizeMentionDisplay(el.getAttribute('data-mention-display') || '');
      const snippet = `@${display}`;
      buf.markup += snippet;
      buf.text += snippet;
      mentions.push({ id, display, plainTextIndex: buf.plainTextIndex });
      buf.plainTextIndex += snippet.length;
      return;
    }

    if (tag === 'img') {
      const alt = String(el.getAttribute('alt') || '').trim();
      const src = String(el.getAttribute('src') || '');
      const t = alt || src;
      buf.markup += t;
      buf.text += t;
      buf.plainTextIndex += t.length;
      return;
    }

    if (tag === 'a' && isAllowedChatLinkHref(el.getAttribute('href'))) {
      const href = String(el.getAttribute('href') || '').trim();
      const innerBuf = { markup: '', text: '', plainTextIndex: buf.plainTextIndex };
      for (let i = 0; i < el.childNodes.length; i++) {
        walkInto(innerBuf, el.childNodes[i]);
      }
      buf.markup += `[${innerBuf.markup}](${href})`;
      buf.text += innerBuf.text;
      buf.plainTextIndex = innerBuf.plainTextIndex;
      return;
    }

    const headingLevel = headingLevelFromTag(tag);
    if (headingLevel) {
      const prefix = `${'#'.repeat(headingLevel)} `;
      const innerBuf = { markup: '', text: '', plainTextIndex: buf.plainTextIndex };
      for (let i = 0; i < el.childNodes.length; i++) {
        walkInto(innerBuf, el.childNodes[i]);
      }
      buf.markup += prefix + innerBuf.markup.trimEnd();
      buf.text += innerBuf.text.trimEnd();
      buf.plainTextIndex = innerBuf.plainTextIndex;
      return;
    }

    const innerMarkers = getMarkers(el);
    if (innerMarkers.length) {
      buf.markup += innerMarkers.join('');
      buf.text += innerMarkers.join('');
      buf.plainTextIndex += innerMarkers.length;
    }
    for (let i = 0; i < el.childNodes.length; i++) {
      walkInto(buf, el.childNodes[i]);
    }
    if (innerMarkers.length) {
      const close = [...innerMarkers].reverse().join('');
      buf.markup += close;
      buf.text += close;
      buf.plainTextIndex += close.length;
    }
  }

  /**
   * Walk direct children with markdown block boundaries: after a block element, or before a
   * block after non-block content, insert \\n\\n so ATX headings do not absorb following text.
   */
  function walkChildrenWithBlockGaps(parentEl) {
    let child = parentEl.firstChild;
    let prev = null;
    while (child) {
      const isBlock =
        child.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((child.tagName || '').toLowerCase());
      const prevIsBlock =
        prev &&
        prev.nodeType === Node.ELEMENT_NODE &&
        BLOCK_TAGS.has((prev.tagName || '').toLowerCase());
      if (prev && (prevIsBlock || (isBlock && !prevIsBlock))) {
        markup += '\n\n';
        text += '\n\n';
        plainTextIndex += 2;
      }
      walk(child);
      prev = child;
      child = child.nextSibling;
    }
  }

  /** Remap mention plainTextIndex when we trim leading/trailing from format content. */
  function remapMentionIndices(mentionsSubset, start, leadingLen, openLen, middleLen, closeLen) {
    mentionsSubset.forEach(m => {
      const bufOffset = m.plainTextIndex - start;
      if (bufOffset < leadingLen) {
        m.plainTextIndex = start + bufOffset;
      } else if (bufOffset < leadingLen + middleLen) {
        m.plainTextIndex = start + leadingLen + openLen + (bufOffset - leadingLen);
      } else {
        m.plainTextIndex =
          start +
          leadingLen +
          openLen +
          middleLen +
          closeLen +
          (bufOffset - leadingLen - middleLen);
      }
    });
  }

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const value = node.textContent || '';
      markup += value;
      text += value;
      plainTextIndex += value.length;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node;
    const tag = (el.tagName || '').toLowerCase();

    if (tag === 'br') {
      markup += '\n';
      text += '\n';
      plainTextIndex += 1;
      return;
    }

    if (tag === mentionTag && el.getAttribute(mentionAttr)) {
      const id = el.getAttribute(mentionAttr);
      const display = normalizeMentionDisplay(el.getAttribute('data-mention-display') || '');
      const snippet = `@${display}`;
      markup += snippet;
      text += snippet;
      mentions.push({ id, display, plainTextIndex });
      plainTextIndex += snippet.length;
      return;
    }

    if (tag === 'img') {
      const alt = String(el.getAttribute('alt') || '').trim();
      const src = String(el.getAttribute('src') || '');
      const t = alt || src;
      markup += t;
      text += t;
      plainTextIndex += t.length;
      return;
    }

    if (tag === 'a' && isAllowedChatLinkHref(el.getAttribute('href'))) {
      let href = String(el.getAttribute('href') || '').trim();
      const innerBuf = { markup: '', text: '', plainTextIndex };
      for (let i = 0; i < el.childNodes.length; i++) {
        walkInto(innerBuf, el.childNodes[i]);
      }

      const innerText = innerBuf.text.trim();
      const normalizedInner = normalizeChatLinkHref(innerText);
      if (isAllowedChatLinkHref(normalizedInner)) {
        href = normalizedInner;
        if (el.getAttribute('href') !== href) {
          el.setAttribute('href', href);
        }
      }

      markup += `[${innerBuf.markup}](${href})`;
      text += innerBuf.text;
      plainTextIndex = innerBuf.plainTextIndex;
      return;
    }

    const walkHeadingLevel = headingLevelFromTag(tag);
    if (walkHeadingLevel) {
      const prefix = `${'#'.repeat(walkHeadingLevel)} `;
      const innerBuf = { markup: '', text: '', plainTextIndex };
      for (let i = 0; i < el.childNodes.length; i++) {
        walkInto(innerBuf, el.childNodes[i]);
      }
      markup += prefix + innerBuf.markup.trimEnd();
      text += innerBuf.text.trimEnd();
      plainTextIndex = innerBuf.plainTextIndex;
      return;
    }

    const markers = getMarkers(el);
    if (markers.length) {
      const startIndex = plainTextIndex;
      const mentionCountBefore = mentions.length;
      const buf = { markup: '', text: '', plainTextIndex: startIndex };
      for (let i = 0; i < el.childNodes.length; i++) {
        walkInto(buf, el.childNodes[i]);
      }
      const innerMarkup = buf.markup;
      const innerText = buf.text;
      const trimmedMarkup = innerMarkup.trimStart();
      const trimmedText = innerText.trimStart();
      const leadingLen = innerMarkup.length - trimmedMarkup.length;
      const leadingSpace = innerMarkup.slice(0, leadingLen);
      const afterEndTrim = trimmedMarkup.trimEnd();
      const trailingLen = trimmedMarkup.length - afterEndTrim.length;
      const trailingSpace = trimmedMarkup.slice(trimmedMarkup.length - trailingLen);
      const middle = afterEndTrim;
      const openStr = markers.join('');
      const closeStr = [...markers].reverse().join('');

      if (middle.length === 0) {
        markup += innerMarkup;
        text += innerText;
        plainTextIndex += innerMarkup.length;
        return;
      }

      markup += leadingSpace;
      text += leadingSpace;
      plainTextIndex += leadingSpace.length;
      markup += openStr;
      text += openStr;
      plainTextIndex += openStr.length;
      markup += middle;
      text += trimmedText.trimEnd();
      plainTextIndex += middle.length;
      markup += closeStr;
      text += closeStr;
      plainTextIndex += closeStr.length;
      markup += trailingSpace;
      text += trailingSpace;
      plainTextIndex += trailingSpace.length;

      const mentionsAdded = mentions.slice(mentionCountBefore);
      if (mentionsAdded.length) {
        remapMentionIndices(
          mentionsAdded,
          startIndex,
          leadingLen,
          openStr.length,
          middle.length,
          closeStr.length,
          trailingLen,
        );
      }
      return;
    }

    walkChildrenWithBlockGaps(el);
  }

  walkChildrenWithBlockGaps(containerEl);

  return { markup, text, mentions };
}
